<template>
    <div v-if="displayData"
        class="h-full bg-white/5 backdrop-blur-lg rounded-2xl p-6 text-white border border-white/10 relative overflow-hidden flex flex-col">
        <div class="flex justify-between items-center mb-6 pb-4 border-b border-white/5">
            <h3 class="text-xl font-bold flex items-center">
                <i :class="displayData.isSprint ? 'fas fa-bolt text-purple-400' : 'fas fa-flag-checkered text-yellow-500'" class="mr-2"></i>
                {{ displayData.isSprint ? 'Ultimo Podio Sprint' : 'Ultimo Podio' }}
            </h3>
            <span class="text-gray-400 text-xs uppercase tracking-widest font-bold bg-white/5 px-2 py-1 rounded">{{
                displayData.raceName }} {{ displayData.season }}</span>
        </div>

        <div class="space-y-3 flex-grow">
            <div v-for="result in displayData.topThree" :key="result.position"
                class="flex items-center bg-white/5 rounded-xl p-4 transition duration-300 hover:bg-white/10 border border-transparent hover:border-white/10 group">
                <div class="font-bold text-2xl w-10 text-center mr-4 bg-black/20 rounded-lg py-2 group-hover:bg-black/40 transition duration-300"
                    :class="getPositionColor(result.position)">
                    {{ result.position }}
                </div>
                <div class="flex-grow">
                    <div class="font-bold text-lg leading-tight">
                        {{ result.Driver.givenName }} <span
                            class="uppercase tracking-wide ml-1">{{ result.Driver.familyName }}</span></div>
                    <div class="text-xs text-gray-400 mt-1 uppercase tracking-wider flex items-center">
                        <span :style="{ backgroundColor: getTeamColor(result.Constructor.constructorId) }"
                            class="w-2 h-2 rounded-full mr-1.5 inline-block shadow-sm"></span>
                        {{ result.Constructor.name }}
                    </div>
                </div>
                <div class="text-sm font-mono text-gray-300 bg-black/20 px-3 py-1 rounded">
                    {{ result.Time ? result.Time.time : '+ ' + result.status }}
                </div>
            </div>
        </div>

        <!-- Show sprint winner if the main display is the race (and sprint exists) -->
        <div v-if="!displayData.isSprint && sprintWinner"
            class="mt-4 pt-4 border-t border-white/5">
            <div class="flex items-center bg-gradient-to-r from-purple-900/30 to-transparent rounded-xl p-4 border border-purple-500/20 hover:border-purple-500/40 transition duration-300 group">
                <div class="mr-4 flex items-center justify-center w-10 h-10 rounded-lg bg-purple-600/30 text-purple-300 group-hover:bg-purple-600/50 transition">
                    <i class="fas fa-bolt text-lg"></i>
                </div>
                <div class="flex-grow">
                    <div class="text-[10px] text-purple-400 uppercase tracking-widest font-bold mb-0.5">Vincitore Sprint – {{ sprintResults.raceName }}</div>
                    <div class="font-bold text-base leading-tight">{{ sprintWinner.Driver.givenName }} <span
                            class="uppercase tracking-wide">{{ sprintWinner.Driver.familyName }}</span></div>
                    <div class="text-xs text-gray-400 mt-0.5 uppercase tracking-wider">{{ sprintWinner.Constructor.name }}</div>
                </div>
                <div class="text-sm font-mono text-purple-300 bg-black/20 px-3 py-1 rounded">
                    {{ sprintWinner.Time ? sprintWinner.Time.time : sprintWinner.status }}
                </div>
            </div>
        </div>
    </div>
    <div v-else
        class="h-full bg-white/5 backdrop-blur-md rounded-2xl p-6 text-white border border-white/10 animate-pulse">
        <div class="h-6 bg-white/10 rounded w-1/3 mb-4"></div>
        <div class="space-y-3">
            <div class="h-16 bg-white/10 rounded-xl"></div>
            <div class="h-16 bg-white/10 rounded-xl opacity-80"></div>
            <div class="h-16 bg-white/10 rounded-xl opacity-60"></div>
        </div>
    </div>
</template>

<script>
import { computed } from 'vue';
import { getTeamColor } from '../utils/teamColors';

export default {
    name: 'LastRaceResult',
    props: {
        results: {
            type: Object,
            default: null
        },
        sprintResults: {
            type: Object,
            default: null
        }
    },
    setup(props) {
        // Determine which event is the most recent: the last race or the last sprint
        const displayData = computed(() => {
            const raceData = props.results;
            const sprintData = props.sprintResults;

            if (!raceData && !sprintData) return null;

            const raceDate = raceData ? new Date(raceData.date) : new Date(0);
            const sprintDate = sprintData ? new Date(sprintData.sprintDate) : new Date(0);

            // If sprint is more recent, show sprint podium
            if (sprintData && sprintDate > raceDate) {
                return {
                    isSprint: true,
                    raceName: sprintData.raceName,
                    season: sprintData.season,
                    topThree: sprintData.results.slice(0, 3)
                };
            }

            // Otherwise show race podium
            if (raceData && raceData.Results) {
                return {
                    isSprint: false,
                    raceName: raceData.raceName,
                    season: raceData.season,
                    topThree: raceData.Results.slice(0, 3)
                };
            }

            return null;
        });

        // Sprint winner to show as a small card below the race podium
        const sprintWinner = computed(() => {
            if (!props.sprintResults || !props.sprintResults.results || props.sprintResults.results.length === 0) return null;
            return props.sprintResults.results[0];
        });

        const getPositionColor = (position) => {
            if (position === '1') return 'text-yellow-400';
            if (position === '2') return 'text-gray-300';
            if (position === '3') return 'text-yellow-700';
            return 'text-white';
        };

        return {
            displayData,
            sprintWinner,
            getPositionColor,
            getTeamColor
        };
    }
}
</script>
